// @ts-strict-ignore
import { BaseArticleHelper } from '@article/helper/base-article.helper';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { Log } from '@shared/debug/log';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { RangeOnLineArticleData } from '../components/article/model/range-on-line-article-data';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { SelectionModelService } from '../components/article/service/selection-model.service';
import { ProjectArticleService } from '../service/project-article.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ArticleContainerService } from '@3d-scene/service/article-container.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class MountingRailHelper extends BaseArticleHelper {
  constructor(
    private selectionModelService: SelectionModelService,
    projectArticleService: ProjectArticleService,
    containerService: ContainerService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private articleContainerUtils: ArticleContainerUtils,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private articleConnectionService: ArticleConnectionService,
    private articleContainerService: ArticleContainerService
  ) {
    super(projectArticleService, containerService);
    this._baseArticleType = ArticleTypeConstants.TYPE_MOUNTING_RAIL;
  }

  public getInsertArticleHelper(): InsertArticleHelper {
    const mountingRailContainer: ArticleContainer = this.getMountingRailContainer();
    return !mountingRailContainer ? null : this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(mountingRailContainer);
  }

  public getRailArticleContainers(): ArticleContainer[] {
    const insertArticleHelper = this.getInsertArticleHelper();
    if(Log.assertExists(insertArticleHelper, 'insertArticleHelper')) {
      return this.getInsertArticleHelper().getAllConnectedArticles();
    }
    return [];
  }

  public getMountingRailContainer(): ArticleContainer {
    const mountingRailUsage: ArticleUsageVO = this.getMountingRailUsage();
    return !mountingRailUsage ? null : this.containerService.findArticleContainerInGlobal(mountingRailUsage.id);
  }

  public getMountingRailUsage(): ArticleUsageVO {
    return super.getBaseArticleUsage();
  }

  public createRangeOnLineArticleDataBySelection(
    rangeOnLineArticleType: string = null,
    considerOnlyConfiguredArticles: boolean = true
  ): RangeOnLineArticleData[] {
    return this.createRangeOnLineArticleDataByArticles(
      this.selectionModelService.getArticleContainers(),
      rangeOnLineArticleType,
      considerOnlyConfiguredArticles
    );
  }

  public createRangeOnLineArticleDataByArticles(
    articleContainers: ArticleContainer[],
    rangeOnLineArticleType: string = null,
    considerOnlyConfiguredArticles: boolean = true
  ): RangeOnLineArticleData[] {
    const rangeOnLineArticleDatas: RangeOnLineArticleData[] = [];
    let rangeOnLineData: RangeOnLineArticleData = null;
    for (const selectedContainer of articleContainers) {
      const baseContainer: ArticleContainer = this.articleContainerService.getBaseRangeOnLineContainer(selectedContainer);
      if (
        !baseContainer ||
        !baseContainer.article.type ||
        (rangeOnLineArticleType && rangeOnLineArticleType !== baseContainer.article.type.displayName)
      ) {
        continue;
      }
      rangeOnLineData = this.createRangeOnLineData(baseContainer, rangeOnLineArticleDatas);
      if (selectedContainer.articleGUID === baseContainer.articleGUID) {
        rangeOnLineData.baseArticleSelected = true;
      }
      if (
        this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(
          selectedContainer,
          baseContainer.articleGUID,
          null,
          considerOnlyConfiguredArticles
        )
      ) {
        rangeOnLineData.storeSelectedArticleOnRail(selectedContainer);
      }
    }
    if (rangeOnLineArticleDatas.length === 0) {
      const rangeOnLineArticleUsages: ArticleUsageVO[] = this.projectArticleService.findArticleUsagesByType(
        rangeOnLineArticleType != null ? rangeOnLineArticleType : this._baseArticleType
      );
      for (const rangeOnLineUsage of rangeOnLineArticleUsages) {
        const rangeOnLineContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(rangeOnLineUsage.id);
        rangeOnLineData = this.createRangeOnLineData(rangeOnLineContainer, rangeOnLineArticleDatas);
        rangeOnLineData.baseArticleSelected = true;
      }
    }
    return rangeOnLineArticleDatas;
  }

  private createRangeOnLineData(baseContainer: ArticleContainer, sourceToStore: RangeOnLineArticleData[]): RangeOnLineArticleData {
    if (!baseContainer) {
      return null;
    }
    let rangeOnLineData: RangeOnLineArticleData = this.findRangeOnLineDataByBaseUsageId(baseContainer.articleGUID, sourceToStore);
    if (!rangeOnLineData) {
      rangeOnLineData = new RangeOnLineArticleData(this.articleContainerUtils, this.insertArticleHelperFactory, baseContainer);
      sourceToStore.push(rangeOnLineData);
    }
    return rangeOnLineData;
  }

  public findRangeOnLineDataByBaseUsageId(articleUsageId: string, source: RangeOnLineArticleData[]): RangeOnLineArticleData {
    if (!articleUsageId || !source) {
      return null;
    }
    for (const rangeOnLineData of source) {
      if (rangeOnLineData.baseUsageId === articleUsageId) {
        return rangeOnLineData;
      }
    }
    return null;
  }

  public getSelectedArticleContainersOnRail(): ArticleContainer[] {
    const selectedArticleContainersOnRail: ArticleContainer[] = [];
    const selectedContainers: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    for (const selectedContainer of selectedContainers) {
      if (!this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(selectedContainer)) {
        continue;
      }
      if (this.compositeArticleInfoUtils.isCompositePartArticle(selectedContainer)) {
        const baseCompositePartArticles: ArticleContainer[] = this.containerService.getBaseCompositePartArticles(selectedContainer);
        if (baseCompositePartArticles.length > 1) {
          continue;
        }
      }
      selectedArticleContainersOnRail.push(selectedContainer);
    }
    return selectedArticleContainersOnRail;
  }

  public selectRail(): void {
    const mountingRailUsage: ArticleUsageVO = this.getMountingRailUsage();
    if (!mountingRailUsage) {
      return;
    }
    this.selectionModelService.selectArticlesByUsageIds([mountingRailUsage.id]);
  }
}
